import axios from "axios";
import moment from "moment";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Card, CardList } from "components/Card";
import Page from "components/Page";
import Constants from "helpers/constants";
import Config from "config";
import { State } from "state";
import { createProject } from "state/create_project";
import { fetchAllProjects } from "state/my_projects";
import { LightThemeTable } from "components/LightThemeTable";
import { Switch, withStyles } from "@material-ui/core";
import { updateProject, deleteProject } from "state/project";
import Loader from "components/Loader";
import eye from "../assets/img/eye.svg";
import { encryptProjectId } from "helpers/encryptUrlId";
import { getItem } from "helpers/storage";
import { getHeaders } from "helpers/headers";
import CommonModal from "components/CommonModal";
import { FileQuery } from "interfaces/file";

type ProjectsProps = RouteComponentProps & PropsFromRedux;


const CustomSwitch = withStyles({
  root: {
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
  },
  switchBase: {
    padding: 2,
    color: "#fff",
    "&$checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + $track": {
        backgroundColor: "#E5B42F",
        opacity: 1,
        border: "none",
      },
    },
  },
  thumb: {
    width: 12,
    height: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
  },
  track: {
    borderRadius: 16 / 2,
    border: "1px solid #ccc",
    backgroundColor: "#ccc",
    opacity: 1,
    transition: "background-color 0.3s ease",
  },
  checked: {},
})(Switch);

interface ProjectsState {
  automated: boolean;
  loading: boolean;
  showDeleteModal: boolean;
  deleteProjectId: any;
  projectName: string;
  isAwaitingCreation: boolean;
  showPublishWarningModal: boolean;
  pendingPublishProjectId: string | null;
}

class Projects extends React.Component<ProjectsProps, ProjectsState> {
  constructor(props: ProjectsProps) {
    super(props);
    this.state = {
      automated: false,
      loading: true,
      showDeleteModal: false,
      deleteProjectId: null,
      projectName: "",
      isAwaitingCreation: false,
      showPublishWarningModal: false,
      pendingPublishProjectId: null,
    };
  }

  handleSwitchChange = async (
    event: { target: { checked: boolean } },
    project_id: string
  ) => {
    const checked = event.target.checked;
    if (checked) {
      try {
        const response = await axios.get(
          `${Config.API_SERVICE_URL}/api/projects/${project_id}/allProjectfiles?size=100000000&page=0`,
          getHeaders()
        );
        const files: FileQuery[] = response.data?.data ?? [];
        const hasNonCompleteFiles = files.some((file: FileQuery) => file.ProjectFileStatus && !["Upload_Complete"].includes(file.ProjectFileStatus));
        if (hasNonCompleteFiles) {
          this.setState({ showPublishWarningModal: true, pendingPublishProjectId: project_id,});
          return;
        }
      } catch (e) {
        console.error(e);
      }
    }
    await this.doPublishUpdate(project_id, checked);
  };
  doPublishUpdate = async (project_id: string, checked: boolean) => {
    this.setState({ automated: checked, loading: true });

    const fields = { published: checked };
    
    try {
      await this.props.updateProject(project_id, fields);
      await this.props.fetchAllProjects();
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  async componentDidMount() {
    this.setState({ loading: true });
    await this.props.fetchAllProjects();
    this.setState({ loading: false });
  }

  public handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const { history } = this.props;

    this.props
      .createProject({ name: Constants.CREATE_PROJECT_DEFAULT_NAME })
      .then(() => {
        const { id } = this.props.create_project || {};

        if (id) history.push(`/create-new-project/${id}`);
      })
      .catch(() => {});
  };

  async confirmDelete() {
    const { deleteProjectId } = this.state;

    this.setState({ loading: true, isAwaitingCreation: true });

    try {
      await this.props.deleteProject(deleteProjectId);
      this.setState({ isAwaitingCreation: false, showDeleteModal: false });
      this.props.fetchAllProjects();
      this.setState({ deleteProjectId: null });
    } catch (error) {
      console.error("Failed to delete project", error);
    } finally {
      this.setState({ loading: false, isAwaitingCreation: false });
    }
  }

  renderPublishWarningModal() {
    const { showPublishWarningModal, pendingPublishProjectId } = this.state;

    const handlePublishAnyway = async () => {
      this.setState({ showPublishWarningModal: false });
      if (pendingPublishProjectId) {
        await this.doPublishUpdate(pendingPublishProjectId, true);
      }
      this.setState({ pendingPublishProjectId: null });
    };

    const handleGoBackToStep2 = () => {
      this.setState({
        showPublishWarningModal: false,
        pendingPublishProjectId: null,
      });
      if (pendingPublishProjectId) {
        this.props.history.push(`/create-new-project/${pendingPublishProjectId}?step=2`);
      }
    };

    const footer = (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button
          onClick={handlePublishAnyway}
          className="primary-button"
        >
          Publish Anyway
        </button>
        <button
          onClick={handleGoBackToStep2}
          className="primary-button"
        >
          Go Back to Step 2
        </button>
      </div>
    );

    return (
      <CommonModal
        show={showPublishWarningModal}
        onClose={() => this.setState({ showPublishWarningModal: false, pendingPublishProjectId: null })}
        title="Store Files Status Check"
        width="400px"
        closeOnBackdropClick={false}
        footer={footer}
      >
        <p style={{ fontSize: "12px", lineHeight: "1.6", margin: 0 }}>
          There are still Store Files (PSA and PDF) not in{" "}
          <strong>Upload_Complete</strong> Status.
          <br />
          <br />
          This could cause confusion for users as Store files will not be
          available to view or download.
        </p>
      </CommonModal>
    );
  }

  renderDeleteModal(projectName: string) {
    if (!this.state.showDeleteModal) return null;

    // Find the project to check if it's published
    const project = this.props.projects?.find(
      (p: any) => p.project_id === this.state.deleteProjectId
    );
    const publishedValue = project?.published as unknown as number;
    const isPublished = publishedValue === 1 || publishedValue === 3;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "24px",
            borderRadius: "12px",
            width: "350px",
            textAlign: "left",
          }}
        >
          <h3 style={{ marginBottom: 20, fontSize: "12px", fontWeight: 600 }}>
            {isPublished ? "Cannot Delete Published Project." : "Are you sure?"}
          </h3>
          <p style={{ marginBottom: 20, fontSize: "12px" }}>
            {isPublished ? (
              <>
                The project{" "}
                <span style={{ fontSize: "12px", fontWeight: 600 }}>
                  {projectName}
                </span>{" "}
                is currently published. To delete this project, please unpublish
                it first.
              </>
            ) : (
              <>
                Do you really want to delete{" "}
                <span style={{ fontSize: "12px", fontWeight: 600 }}>
                  {projectName}
                </span>{" "}
                project?
              </>
            )}
          </p>

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            <div style={{ width: '7%', paddingTop: '3px' }}>
                  {this.state.isAwaitingCreation && (<Loader height={20} width={20} custom={true} ></Loader>)}
                </div>
            {!isPublished && (
              <button
                onClick={() => this.confirmDelete()}
                style={{
                  background: "#E5B611",
                  color: "#000",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                Yes, Delete
              </button>
            )}

            <button
              onClick={() => this.setState({ showDeleteModal: false })}
              style={{
                background: "#E5B611",
                color: "#000",
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {isPublished ? "OK" : "No"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  private getChainValue = (chainValue: any): string => {
    const normalize = (value: any): string => {
      if (typeof value === "string") return value.trim();
      if (value && typeof value === "object") {
        return String(value.chain_name ?? "").trim();
      }
      return String(value ?? "").trim();
    };

    return Array.isArray(chainValue)
      ? chainValue.map(normalize).filter(Boolean).join(", ")
      : normalize(chainValue);
  };

  public renderProjects() {
    const { projects } = this.props;
    const projectsWithChainSort = (projects || []).map((p: any) => ({ ...p,
      _chain_sort: ((v) => v && v !== "-" ? v.toLowerCase() : "zzzz")(this.getChainValue(p.chain_name)) }));
    return (
      <LightThemeTable
        data={projectsWithChainSort as object[]}
        columns={[
          {
            field: "_chain_sort",
            header: "Chains",
            dataQa: "chain_name",
            // className: "project-chain-name",
            formatter: (_value: any, row: any) => this.getChainValue(row.chain_name) || "-",
          },
          {
            field: "project_name",
            header: "Project Name",
            dataQa: "project_name",
          },
          {
            field: "project_type",
            header: "Project Type",
            dataQa: "project_type",
          },
          {
            field: "published",
            header: "Status",
            dataQa: "project_status_pill",
            formatter: (value, row : any) => {
              let pillProps;

              if (row.published === 2) {
                pillProps = {
                  text: "Unpublished - Files Processing",
                  backgroundColor: "#FFF8E1",
                  color: "#F48120",
                };
              } else if (row?.published === 1) {
                pillProps = {
                  text: "Published",
                  backgroundColor: "#E8F5E9",
                  color: "#24A148",
                };
              } else if(row.published === 3){
                pillProps = {
                  text: "Published - Files Processing",
                  backgroundColor: "#FFF8E1",
                  color: "#F48120",
                };
              }else{
                pillProps = {
                  text: "Unpublished",
                  backgroundColor: "#FFEBEE",
                  color: "#E01F29",
                };
              }
              return (
                <div
                  style={{
                    backgroundColor: pillProps.backgroundColor,
                    color: pillProps.color,
                    padding: "1px 4px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    display: "inline-block",
                    textAlign: "center",
                    minWidth: "100px",
                    textTransform: "capitalize",
                  }}
                >
                  {pillProps.text}
                </div>
              );
            },
          },
          {
            field: "updated_at",
            header: "Updated Date",
            formatter: (value) => moment(value).format("M/D/YYYY"),
            dataQa: "project_updatedate",
          },
          {
            field: "actions",
            header: "",
            formatter: (_: any, row: any) => {
            const isAdmin = this.props.user?.role === Constants.AB_SYSTEM_ADMIN;
              function decrypt(project_id: any) {
                throw new Error("Function not implemented.");
              }

            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                {!isAdmin && <button
                  onClick={() => {
                      const { history } = this.props;
                      const chainAccess = getItem('chainRole');
                      const parseData = JSON.parse(chainAccess)
                      const roleId = parseData?.role_id;
                      const projectId = encryptProjectId(row.project_id);
                      history.push(`/manage-all-data/projects/projects-new/${projectId}/${roleId}`);
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer" }}
                  >
                  <img src={ eye } style={{minWidth: "20px"}} alt="View"/>
                </button>}
                {isAdmin && <button
                  onClick={() => {
                      const { history } = this.props;
                      const roleId = Constants.AB_SYSTEM_ADMIN_ID;
                      const projectId = encryptProjectId(row.project_id);
                      history.push(`/manage-all-data/projects/projects-new/${projectId}/${roleId}`);
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer" }}
                  >
                  <img src={ eye } style={{minWidth: "20px"}} alt="View"/>
                </button>}
                <button
                  onClick={() => {
                    const { history } = this.props;
                    history.push(`/create-new-project/${row.project_id}`);
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer" }}
                >
                  <img style={{minWidth: "14px"}} src={require("../assets/img/Edit.png")} alt="Edit" />
                </button>

              {isAdmin && (
                <button
                  onClick={() =>
                    this.setState({
                      showDeleteModal: true,
                      deleteProjectId: row.project_id,
                      projectName: row?.project_name,
                    })
                  }
                  style={{ background: "none", border: "none", cursor: "pointer" }}
                >
                  <img style={{minWidth: "14px"}} src={require("../assets/img/Delete.png")} alt="Delete" />
                </button>
              )}

                <div style={{ marginTop: "20px" }}>
                  <CustomSwitch
                    checked={row.published}
                    onChange={(e) => this.handleSwitchChange(e, row.project_id)}
                  />
                </div>
              </div>
            )},
          },
        ]}
        isLoading={this.state.loading || this.props.loading}
        rowStyle={(row, idx) => {
          if (row.published === 2) {
            return { backgroundColor: "#E5E7EB", color: "#6B7280" };
          } else if (row.status === "inactive") {
            return { backgroundColor: "#F0F2F5", color: "#6B7280" };
          } else {
            return {};
          }
        }}
      />
    );
  }

  public render() {
    return (
      <div>

        <Page
          style={{
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "16px",
          }}
        >
          <div className="projects-header">
            <h2>Projects</h2>
            <button
              onClick={this.handleClick}
              data-qa="create-project-button"
              className="primary-button"
            >
              Create New Project
            </button>
          </div>

          <CardList>
            <Card>{this.renderProjects()}</Card>
          </CardList>
        </Page>

        {/* FIXED — pass projectName from state */}
        {this.renderDeleteModal(this.state.projectName)}
        {this.renderPublishWarningModal()}
      </div>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    projects: state.my_projects.content,
    loading: state.my_projects.loading,
    create_project: state.create_project.content,
    user: state.auth_user.content,
  }),
  { fetchAllProjects, createProject, updateProject, deleteProject }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default withRouter(connecter(Projects));
